
import LotteryAddressDialog from "@/components/xcoins/lottery/LotteryAddressDialog.vue";
import XcoinsStatusDialog from "@/components/xcoins/StatusDialog.vue";
import {
  getAddressId,
  getAddressMobile,
  getAllAddress,
  getReceiverName,
  isDefaultAddress,
  maskMobile,
  validateSelectedAddress,
} from "@/utils/mallAddress";
import { getExchangeOrderId, getPreviewNumber, pickOrderList } from "@/utils/mallExchange";
import {
  canPurchaseAtSite,
  formatPoints,
  getCoverUrl,
  getDeliveryNote,
  getPrimaryGoodsDetail,
  getProductDescription,
  getProductPoints,
  getProductTitle,
  getSpecText,
  isPhysicalGoods,
  isPointsApiSuccess,
  isProductOutOfStock,
  normalizeMallGoodsList,
} from "@/utils/mallGoods";
import { ensureMallSite, getMallCountry } from "@/utils/mallSite";
import {
  getPointsApiErrorBody,
  resolveMallRedeemError,
  usesRedeemErrorMsgTip,
} from "@/utils/mallRedeemErrors";

export default {
  name: "XcoinsOrderConfirm",
  components: { LotteryAddressDialog, XcoinsStatusDialog },
  layout: "DefaultLayout",
  head() {
    return { title: this.$t("xcoins.ORDER_CONFIRM") };
  },
  data() {
    return {
      product: null,
      preview: null,
      submitting: false,
      loading: true,
      coverFailed: false,
      addressBookVisible: false,
      selectedAddress: null,
      addressError: "",
      lastSubmitAt: 0,
      redeemBlockKind: "", // unavailable / outOfStock / insufficientPoints / stationNotMatch / purchaseLimit
      redeemBlockMsg: "", // 业务失败页内提示只认接口 res.msg
      statusDialog: {
        visible: false,
        type: "error",
        title: "",
        message: "",
        primaryText: "",
        secondaryText: "",
        primaryAction: "",
      },
    };
  },
  computed: {
    goodsId() {
      return this.$route.query.id;
    },
    productTitle() {
      return this.product ? getProductTitle(this.product) : "-";
    },
    productDesc() {
      return this.product ? getProductDescription(this.product) : "";
    },
    specText() {
      return this.product ? getSpecText(this.product) : "";
    },
    deliveryNote() {
      return this.product ? getDeliveryNote(this.product) : "";
    },
    productPoints() {
      const fromPreview = getPreviewNumber(this.preview, "productPoints");
      if (fromPreview !== null) {
        return fromPreview;
      }
      return this.product ? getProductPoints(this.product) : 0;
    },
    /** 账户余额只认兑换预览 currentBalance */
    currentBalance() {
      const fromPreview = getPreviewNumber(this.preview, "currentBalance");
      return fromPreview !== null ? fromPreview : 0;
    },
    payPoints() {
      const fromPreview = getPreviewNumber(this.preview, "payPoints");
      return fromPreview !== null ? fromPreview : this.productPoints;
    },
    /** 兑换后余额只认预览 afterBalance；缺失时用余额减应付兜底 */
    afterBalance() {
      const fromPreview = getPreviewNumber(this.preview, "afterBalance");
      if (fromPreview !== null) {
        return fromPreview;
      }
      return this.currentBalance - this.payPoints;
    },
    /** 是否够兑只认预览 sufficient === true */
    isSufficient() {
      return Boolean(this.preview && this.preview.sufficient === true);
    },
    coverUrl() {
      return this.product ? getCoverUrl(this.product) : "";
    },
    isPhysical() {
      return isPhysicalGoods(this.product);
    },
    selectedAddressId() {
      return this.selectedAddress ? getAddressId(this.selectedAddress) : "";
    },
    inStock() {
      return this.product && !isProductOutOfStock(this.product);
    },
    canSubmit() {
      if (!this.product || this.submitting || !this.inStock || !canPurchaseAtSite(this.product)) {
        return false;
      }
      if (!this.isSufficient || this.redeemBlockKind === "insufficientPoints" || this.redeemBlockKind === "purchaseLimit") {
        return false;
      }
      if (this.isPhysical && !this.selectedAddress) {
        return false;
      }
      return true;
    },
    confirmButtonText() {
      if (this.submitting) {
        return this.$t("xcoins.REDEEMING");
      }
      if (!this.product || !canPurchaseAtSite(this.product) || this.redeemBlockKind === "unavailable" || this.redeemBlockKind === "purchaseLimit") {
        return this.$t("xcoins.UNAVAILABLE_REDEEM");
      }
      if (!this.inStock) {
        return this.$t("xcoins.RESTOCKING");
      }
      return this.$t("xcoins.CONFIRM_REDEEM");
    },
    actionTip() {
      if (!this.product) {
        return "";
      }
      if (usesRedeemErrorMsgTip(this.redeemBlockKind) && this.redeemBlockMsg) {
        return this.redeemBlockMsg;
      }
      if (!canPurchaseAtSite(this.product)) {
        return this.$t("xcoins.SITE_NOT_SUPPORTED");
      }
      if (this.redeemBlockKind === "insufficientPoints" || (this.preview && this.preview.sufficient === false)) {
        return this.$t("xcoins.INSUFFICIENT_TO_REDEEM");
      }
      return "";
    },
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    formatPoints,
    getReceiverName,
    getAddressMobile,
    getAllAddress,
    isDefaultAddress,
    maskMobile,
    /** 返回商品详情；无商品 id 时回商城列表 */
    goBack() {
      if (this.goodsId) {
        this.$router.replace(`/xcoins/goods/${this.goodsId}`);
        return;
      }
      this.$router.replace("/xcoins/exchange");
    },
    /** 提交中不允许再改地址，避免兑换中途换单 */
    openAddressBook() {
      if (this.submitting) {
        return;
      }
      this.addressBookVisible = true;
    },
    /** 地址簿选中或新增成功后作为本次兑换快照 */
    handleAddressConfirm(address) {
      this.selectedAddress = address || null;
      this.addressError = "";
      this.addressBookVisible = false;
    },
    /** 校验收货信息失败时滚到地址区，方便改完再提交 */
    scrollToAddressError() {
      this.$nextTick(() => {
        const el = this.$refs.addressSection;
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    openStatusDialog(options = {}) {
      this.statusDialog = {
        visible: true,
        type: options.type || "error",
        title: options.title || "",
        message: options.message || "",
        primaryText: options.primaryText || this.$t("xcoins.I_KNOW"),
        secondaryText: options.secondaryText || "",
        primaryAction: options.primaryAction || "",
      };
    },
    closeStatusDialog() {
      this.statusDialog.visible = false;
    },
    handleStatusPrimary() {
      const action = this.statusDialog.primaryAction;
      this.closeStatusDialog();
      if (action === "backToShop") {
        this.$router.push("/xcoins/exchange");
      }
    },
    /**
     * 提交前强校验：站点可兑、有库存、余额够；实物还必须有合法地址。
     * @returns {string} 失败文案，空字符串表示可以提交
     */
    validateBeforeSubmit() {
      if (!this.product) {
        return this.$t("page.NETWORK_ERROR");
      }
      if (!canPurchaseAtSite(this.product)) {
        return this.$t("xcoins.SITE_NOT_SUPPORTED");
      }
      if (!this.inStock) {
        return this.$t("xcoins.RESTOCKING");
      }
      if (!this.isSufficient) {
        return this.$t("page.INSUFFICIENT_XCOINS");
      }
      if (this.isPhysical) {
        const errorKey = validateSelectedAddress(this.selectedAddress, getMallCountry());
        if (errorKey) {
          this.addressError = this.$t(`xcoins.${errorKey}`);
          this.scrollToAddressError();
          return this.addressError;
        }
      }
      this.addressError = "";
      return "";
    },
    /** 进入确认页时拉商品与兑换预览；积分展示只认 preview，不请求 /points/detail */
    async bootstrap() {
      this.loading = true;
      this.redeemBlockKind = "";
      this.redeemBlockMsg = "";
      try {
        await ensureMallSite(this.$cookies);
        const res = await this.$global.pointsMallGoodsDetail(this.goodsId).catch(() => null);
        this.product = isPointsApiSuccess(res) && res.data && res.data.id
          ? normalizeMallGoodsList([res.data])[0]
          : null;
        if (!this.product) {
          return;
        }
        await this.fetchPreview();
      } finally {
        this.loading = false;
      }
    },
    /** 用规格 id 预览扣减后余额，成功码只认 "200"；id 无效则不请求 */
    async fetchPreview() {
      const payload = this.buildExchangeIds(getPrimaryGoodsDetail(this.product));
      if (!payload.detailId || !payload.goodsId) {
        this.preview = null;
        return;
      }
      let res = null;
      try {
        res = await this.$pointsCache.requestWithPointToken((token) => {
          return this.$global.pointsMallExchangePreview(payload, token);
        });
      } catch (e) {
        res = getPointsApiErrorBody(e);
      }
      if (isPointsApiSuccess(res) && res.data) {
        this.preview = res.data;
        if (res.data.sufficient === false) {
          this.redeemBlockKind = "insufficientPoints";
        }
        return;
      }
      this.preview = null;
      this.applyRedeemErrorFromRes(res);
    },
    /** 兑换/预览的 goodsId、detailId 只传非空字符串 */
    buildExchangeIds(detail = {}) {
      return {
        detailId: String(this.$route.query.detailId || detail.id || "").trim(),
        goodsId: String(detail.goodsId || (this.product && this.product.id) || "").trim(),
      };
    },
    /**
     * 组装兑换请求。addressId / detailId / goodsId 按接口要求传字符串。
     */
    buildExchangePayload() {
      const payload = {
        ...this.buildExchangeIds(getPrimaryGoodsDetail(this.product)),
        country: getMallCountry(),
      };
      if (this.isPhysical) {
        payload.addressId = String(getAddressId(this.selectedAddress) || "").trim();
      }
      return payload;
    },
    /** 兑换成功后只进结果页，不再去个人中心 */
    goToRedeemResult(orderId) {
      this.$router.replace({
        path: `/xcoins/result/${orderId}`,
        query: this.product && this.product.id ? { goodsId: String(this.product.id) } : {},
      });
    },
    /**
     * 兑换响应没有可用 id 时，取刚生成的最新一单，保证仍能进结果页。
     */
    async fetchLatestExchangeOrderId() {
      const res = await this.$pointsCache.requestWithPointToken((token) => {
        return this.$global.pointsMallOrderPage({ page: 1, limit: 1 }, token);
      }).catch(() => null);
      if (!isPointsApiSuccess(res)) {
        return "";
      }
      const { list } = pickOrderList(res);
      return getExchangeOrderId(list[0]);
    },
    /** 兑换已成功：必须跳转 /xcoins/result/:id，禁止回个人中心 */
    async goAfterExchangeSuccess(orderId) {
      let resolvedId = orderId;
      if (!resolvedId) {
        resolvedId = await this.fetchLatestExchangeOrderId();
      }
      if (!resolvedId) {
        this.$message.success(this.$t("xcoins.REDEMPTION_SUCCESSFUL"));
        return;
      }
      this.goToRedeemResult(resolvedId);
    },
    openRedeemFailedDialog(message) {
      this.openStatusDialog({
        type: "error",
        title: this.$t("xcoins.REDEEM_FAILED"),
        message: message || this.$t("page.REDEMPTION_FAILED"),
      });
    },
    /** 按兑换业务码改库存/站点/积分态，弹窗文案只认 res.msg */
    applyRedeemErrorFromRes(res) {
      const { kind, product, message } = resolveMallRedeemError(res, this.product);
      if (!kind) {
        return "";
      }
      this.redeemBlockKind = kind;
      this.redeemBlockMsg = message;
      if (product) {
        this.product = product;
      }
      if (kind === "insufficientPoints" && this.preview) {
        this.preview = { ...this.preview, sufficient: false };
      }
      return kind;
    },
    /** 下架/站点不匹配走不可兑弹窗；库存/积分走兑换失败弹窗 */
    openRedeemErrorDialog(res, kind) {
      const message = (res && res.msg) || this.$t("page.REDEMPTION_FAILED");
      if (kind === "stationNotMatch" || kind === "unavailable") {
        this.openStatusDialog({
          type: "warning",
          title: this.$t("xcoins.UNAVAILABLE_REDEEM"),
          message,
          secondaryText: this.$t("page.CANCEL"),
          primaryText: this.$t("xcoins.BACK_TO_SHOP"),
          primaryAction: "backToShop",
        });
        return;
      }
      this.openRedeemFailedDialog(message);
    },
    /**
     * 确认兑换：500ms 防抖 + 按钮 loading。
     * 只认 res.code === "200" 为成功；成功后不再用 data.id 是否为空去弹失败。
     */
    async confirmExchange() {
      if (!this.product || this.submitting) {
        return;
      }
      const now = Date.now();
      if (now - this.lastSubmitAt < 500) {
        return;
      }
      this.lastSubmitAt = now;

      if (!this.$store.state.isLogin) {
        this.$message.error(this.$t("page.LOGIN_TO_REDEEM"));
        return;
      }

      const reason = this.validateBeforeSubmit();
      if (reason) {
        if (!canPurchaseAtSite(this.product)) {
          this.openStatusDialog({
            type: "warning",
            title: this.$t("xcoins.UNAVAILABLE_REDEEM"),
            message: reason,
            secondaryText: this.$t("page.CANCEL"),
            primaryText: this.$t("xcoins.BACK_TO_SHOP"),
            primaryAction: "backToShop",
          });
          return;
        }
        if (!this.addressError) {
          this.$message.error(reason);
        }
        return;
      }

      this.submitting = true;
      let orderId = "";
      let succeeded = false;
      try {
        const res = await this.$pointsCache.requestWithPointToken((token) => {
          return this.$global.pointsMallExchange(this.buildExchangePayload(), token);
        });
        // 成功只认 "200"；此处不能 return，否则 finally 之后的跳转不会执行
        if (isPointsApiSuccess(res)) {
          succeeded = true;
          orderId = getExchangeOrderId(res.data);
        } else {
          const kind = this.applyRedeemErrorFromRes(res);
          this.openRedeemErrorDialog(res, kind);
        }
      } catch (e) {
        const body = getPointsApiErrorBody(e);
        const kind = this.applyRedeemErrorFromRes(body);
        this.openRedeemErrorDialog(body, kind);
      } finally {
        this.submitting = false;
      }

      if (succeeded) {
        await this.goAfterExchangeSuccess(orderId);
      }
    },
  },
};
